import React from 'react';
import { Home, Building, Key } from 'lucide-react';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import SelectOption from '../ui/SelectOption';
import Disclaimer from '../ui/Disclaimer';
import { DISCLAIMER_CONTENT } from '../../utils/disclaimerContent';
import { useFirstStepDisclaimer } from '../../hooks/useFirstStepDisclaimer';

const PROPERTY_STATUSES = [
  { 
    value: 'own_outright', 
    label: 'Own Outright',
    icon: Key,
    description: 'You own your home with no mortgage'
  },
  { 
    value: 'own_with_mortgage', 
    label: 'Own with Mortgage',
    icon: Home,
    description: 'You own your home and have a mortgage'
  },
  { 
    value: 'rent', 
    label: 'Rent',
    icon: Building,
    description: 'You rent your residence'
  }
] as const;

type PropertyStatusFormProps = {
  formData: FormData;
  onSubmit: (data: Partial<FormData>) => void;
};

export default function PropertyStatusForm({ formData, onSubmit }: PropertyStatusFormProps) {
  const { shouldShow: showDisclaimer } = useFirstStepDisclaimer();

  return (
    <div id="property-status-form" className="space-y-6">
      <div className="text-center">
        <Title 
          id="property-status-title"
          name="property-status-title"
          as="h1" 
          className="mb-2"
        >
          Property Status
        </Title>
        <Description 
          id="property-status-description"
          name="property-status-description"
          size="lg"
        >
          What's your current living situation?
        </Description>
      </div>

      <div className="grid gap-3">
        {PROPERTY_STATUSES.map(({ value, label, icon, description }) => (
          <SelectOption
            key={value}
            id={`property-status-option-${value}`}
            name={`property-status-option-${value}`}
            icon={icon}
            label={label}
            description={description}
            onClick={() => onSubmit({ propertyStatus: value })}
          />
        ))}
      </div>

      {showDisclaimer && (
        <Disclaimer
          id="property-status-disclaimer"
          name="property-status-disclaimer"
        >
          {DISCLAIMER_CONTENT}
        </Disclaimer>
      )}
    </div>
  );
}